import React, { useState, useEffect, useMemo } from 'react';
// Removed framer-motion animations
import { auth, db } from '../firebase';
import { doc, updateDoc, getDoc, serverTimestamp, setDoc, onSnapshot } from 'firebase/firestore';
import { User } from 'firebase/auth';
import SectionCard from './ui/SectionCard';
import { computeProfileCompletion } from '../lib/profileCompletion';
import { UserProfile } from '../types';
import RichTextEditor from './RichTextEditor';
import FavoriteLocationsManager from './FavoriteLocationsManager';
import { ProfileIcon, ShareIcon } from './icons';
import { Layout } from 'lucide-react';
import { geocodeAddress } from '../services/geocodingService';
import { canSeePlatformOnlineStats } from '../utils/platformAdmin';

interface ProfileSettingsProps {
  user: User | null;
  userProfile: any;
  onClose?: () => void;
  onProfileUpdate?: (updatedProfile: any) => void;
  initialSection?: string; // Nieuwe prop voor initiële sectie
  embedded?: boolean;
}

interface EditableProfileData {
  // Editable fields
  firstName: string; // ✅ FIX: Voornaam toegevoegd
  lastName: string; // ✅ FIX: Achternaam toegevoegd
  displayName: string; // ✅ FIX: Computed from firstName + lastName
  bio: string;
  company: string;
  companyLocation: string; // ✅ FIX: Toegevoegd
  website: string;
  location: string;
  postcode: string; // ✅ FIX: Toegevoegd
  city: string; // ✅ FIX: Toegevoegd
  region: string; // ✅ FIX: Toegevoegd
  country: string; // ✅ FIX: Toegevoegd
  workScope: 'lokaal' | 'nationaal' | 'internationaal'; // ✅ FIX: Toegevoegd
  workScopeCountry: string; // ✅ FIX: Toegevoegd
  phone: string;
  linkedin: string;
  twitter: string;
  expertise: string[];
  interests: string[];
  learningGoals: string[]; // ✅ FIX: Leerdoelen toegevoegd
  languages: string[];
  selectedRegions: string[]; // ✅ FIX: Geselecteerde regio's voor werkgebied
  availability: {
    days: string[];
    timeBlocks: Array<{
      start: string;
      end: string;
      tz: string;
    }>;
  };
  // ✅ FIX: Goals & Doelstellingen
  goals?: {
    clients?: boolean;
    partnerships?: boolean;
    hiring?: boolean;
    funding?: boolean;
    speaking?: boolean;
    content?: boolean;
    personalBrand?: boolean;
    productLaunch?: boolean;
    community?: boolean;
    networking?: boolean;
    learning?: boolean;
    timeframe?: string;
    kpis?: {
      leads?: number;
      revenue?: number;
      followers?: number;
    };
    notes?: string;
  };
  // ✅ FIX: Content voorkeuren
  contentPrefs?: {
    formatPrefs?: string[];
    timeBudget?: string;
    notes?: string;
  };
  // ✅ FIX: Event voorkeuren
  eventPrefs?: {
    mode?: 'online' | 'hybride' | 'fysiek';
    regions?: string[];
    themes?: string[];
    speakerIntent?: boolean;
    hostIntent?: boolean;
  };
  // ✅ FIX: Match voorkeuren
  matchPrefs?: {
    prefer?: 'clients' | 'partners' | 'sparring';
    ambition?: 'beginner' | 'groeiend' | 'scale-up' | 'expert';
    rewards?: string[];
  };
  socialSharingEnabled: boolean;
  sharingFrequency: 3 | 5 | 7;
  // Community share (Together Strong)
  communityShareEnabled?: boolean;
  communityShareGroup?: string;
  communityShareBlocked: string[];
  // Showcase editor fields (Mijn Verhaal)
  showcase?: {
    profile?: ShowcaseItemForm;
    story?: ShowcaseItemForm;
    business?: ShowcaseItemForm;
    engagement?: ShowcaseItemForm;
    partnerships?: ShowcaseItemForm;
    community?: ShowcaseItemForm;
    [key: string]: ShowcaseItemForm | string[] | undefined; // Custom cards + order
    order?: Array<'profile' | 'story' | 'business' | 'engagement' | 'partnerships' | 'community' | string>;
  };
  // Favoriete locaties voor meetings
  favoriteLocations?: Array<{
    id: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    type?: 'restaurant' | 'cafe' | 'office' | 'public' | 'other';
    createdAt: Date | string;
    usageCount?: number;
  }>;
}

type ShowcaseItemForm = {
  title?: string;
  subtitle?: string;
  text?: string;
  imageUrl?: string;
  videoUrl?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user, userProfile, onClose, onProfileUpdate, initialSection, embedded = false }) => {
  const [cardOrder, setCardOrder] = useState<string[]>([]);
  // Movement-style: overall settings sidebar state
  type SettingsKey = 'profileInfo' | 'sharing' | 'cards';
  const [activeSettingsKey, setActiveSettingsKey] = useState<SettingsKey>('profileInfo');
  const settingsList: Array<{ key: SettingsKey; title: string; subtitle: string; icon: React.ReactNode }> = [
    { key: 'profileInfo', title: 'Profiel Instellingen', subtitle: 'Bio, bedrijf, website, locatie', icon: <ProfileIcon size={18} weight="regular" className="text-gray-500" /> },
    { key: 'sharing', title: 'Social Sharing', subtitle: 'Automatisch delen', icon: <ShareIcon size={18} weight="regular" className="text-gray-500" /> },
    { key: 'cards', title: 'Kaarten Beheer', subtitle: 'Open kaarten editor', icon: <Layout size={18} className="text-gray-500" /> },
  ];
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRegionSection, setExpandedRegionSection] = useState<'nederland' | 'internationaal' | null>('nederland'); // ✅ FIX: State voor regio sectie expand/collapse
  
  // ✅ FIX: Regio constanten
  const NEDERLAND_REGIONS = [
    'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Flevoland',
    'Gelderland', 'Overijssel', 'Drenthe', 'Friesland',
    'Groningen', 'Noord-Brabant', 'Limburg', 'Zeeland'
  ];
  
  const INTERNATIONAL_REGIONS = [
    'België - Vlaanderen', 'België - Wallonië', 'Duitsland',
    'Frankrijk', 'Verenigd Koninkrijk', 'Internationaal'
  ];
  const [formData, setFormData] = useState<EditableProfileData>({
    firstName: '', // ✅ FIX: Voornaam toegevoegd
    lastName: '', // ✅ FIX: Achternaam toegevoegd
    displayName: '', // ✅ FIX: DisplayName toegevoegd
    bio: '',
    company: '',
    companyLocation: '', // ✅ FIX: Toegevoegd
    website: '',
    location: '',
    postcode: '', // ✅ FIX: Toegevoegd
    city: '', // ✅ FIX: Toegevoegd
    region: '', // ✅ FIX: Toegevoegd
    country: '', // ✅ FIX: Toegevoegd
    workScope: 'lokaal', // ✅ FIX: Toegevoegd
    workScopeCountry: '', // ✅ FIX: Toegevoegd
    phone: '',
    linkedin: '',
    twitter: '',
    expertise: [],
    interests: [],
    learningGoals: [], // ✅ FIX: Leerdoelen toegevoegd
    languages: [],
    selectedRegions: [], // ✅ FIX: Geselecteerde regio's
    availability: {
      days: [],
      timeBlocks: []
    },
    goals: {}, // ✅ FIX: Goals geïnitialiseerd
    contentPrefs: {}, // ✅ FIX: Content voorkeuren geïnitialiseerd
    eventPrefs: {}, // ✅ FIX: Event voorkeuren geïnitialiseerd
    matchPrefs: {}, // ✅ FIX: Match voorkeuren geïnitialiseerd
    socialSharingEnabled: false,
    sharingFrequency: 3,
    communityShareEnabled: false,
    communityShareGroup: '',
    communityShareBlocked: [],
    showcase: {
      profile: {},
      story: {},
      business: {},
      engagement: {},
      partnerships: {},
      order: ['profile','story','business','engagement','partnerships']
    },
    favoriteLocations: []
  });

  // ✅ FIX: Real-time listener for userProfiles collection (bidirectional sync with network page)
  useEffect(() => {
    if (!user?.uid) return;
    
    const userProfilesRef = doc(db, 'userProfiles', user.uid);
    
    // Real-time listener for userProfiles changes (from network page)
    const unsubscribeUserProfiles = onSnapshot(userProfilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const updatedData = snapshot.data();
        console.log('🔄 Real-time update from userProfiles:', updatedData);
        
        // Update formData with new data from network page
        setFormData(prev => ({
          ...prev,
          selectedRegions: updatedData.selectedRegions || prev.selectedRegions,
          expertise: updatedData.expertise || prev.expertise,
          interests: updatedData.interests || prev.interests,
          learningGoals: updatedData.learningGoals || prev.learningGoals,
          bio: updatedData.bio || prev.bio,
          company: updatedData.company || prev.company,
          companyLocation: updatedData.companyLocation || prev.companyLocation,
          location: updatedData.location || prev.location,
          postcode: updatedData.postcode || prev.postcode,
          city: updatedData.city || prev.city,
          region: updatedData.region || prev.region,
          country: updatedData.country || prev.country,
          workScope: updatedData.workScope || prev.workScope,
          workScopeCountry: updatedData.workScopeCountry || prev.workScopeCountry,
        }));
      }
    }, (error) => {
      console.error('❌ Error in userProfiles real-time listener:', error);
    });
    
    return () => {
      unsubscribeUserProfiles();
    };
  }, [user?.uid]);
  
  // ✅ FIX: Real-time listener for profiles collection (bidirectional sync)
  useEffect(() => {
    if (!user?.uid) return;
    
    const profilesRef = doc(db, 'profiles', user.uid);
    
    // Real-time listener for profiles changes
    const unsubscribeProfiles = onSnapshot(profilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const updatedData = snapshot.data();
        console.log('🔄 Real-time update from profiles:', updatedData);
        
        // Update formData with new data
        setFormData(prev => ({
          ...prev,
          goals: updatedData.goals || prev.goals,
          learningGoals: updatedData.learningGoals || prev.learningGoals,
          contentPrefs: updatedData.contentPrefs || prev.contentPrefs,
          eventPrefs: updatedData.eventPrefs || prev.eventPrefs,
          matchPrefs: updatedData.matchPrefs || prev.matchPrefs,
          availability: updatedData.availability || prev.availability,
        }));
      }
    }, (error) => {
      console.error('❌ Error in profiles real-time listener:', error);
    });
    
    return () => {
      unsubscribeProfiles();
    };
  }, [user?.uid]);

  // Load profile data from both userProfiles and profiles collections
  useEffect(() => {
    const loadAllProfileData = async () => {
      if (!user || !userProfile) return;
      
      try {
        // ✅ FIX: Load data from 'profiles' collection (where goals, business, etc. are stored)
        const profilesRef = doc(db, 'profiles', user.uid);
        const profilesSnap = await getDoc(profilesRef);
        const profilesData = profilesSnap.exists() ? profilesSnap.data() : {};
        
        console.log('📊 Loaded data from profiles collection:', profilesData);
        
        // ✅ FIX: Merge data from both collections (profiles takes priority for overlapping fields)
        const mergedData = {
          ...userProfile,
          ...profilesData,
          // Goals from profiles collection
          goals: profilesData.goals || userProfile.goals || {},
          // Learning goals from profiles collection
          learningGoals: profilesData.learningGoals || userProfile.learningGoals || [],
          // Availability from profiles collection (can be nested in availability object)
          availability: profilesData.availability?.days 
            ? profilesData.availability 
            : (userProfile.availability || { days: [], timeBlocks: [] }),
          // Content prefs from profiles collection
          contentPrefs: profilesData.contentPrefs || userProfile.contentPrefs || {},
          // Event prefs from profiles collection
          eventPrefs: profilesData.eventPrefs || userProfile.eventPrefs || {},
          // Match prefs from profiles collection
          matchPrefs: profilesData.matchPrefs || userProfile.matchPrefs || {},
        };
        
        // ✅ Compute displayName from firstName + lastName
        const firstName = mergedData.firstName || mergedData.onboardingData?.firstName || '';
        const lastName = mergedData.lastName || mergedData.onboardingData?.lastName || '';
        const computedDisplayName = mergedData.displayName || mergedData.onboardingData?.displayName || `${firstName} ${lastName}`.trim();
        
        setFormData({
          firstName,
          lastName,
          displayName: computedDisplayName,
          bio: mergedData.bio || '',
          company: mergedData.company || '',
          companyLocation: mergedData.companyLocation || '',
          website: mergedData.website || '',
          location: mergedData.location || '',
          postcode: mergedData.postcode || '',
          city: mergedData.city || '',
          region: mergedData.region || '',
          country: mergedData.country || '',
          workScope: mergedData.workScope || 'lokaal',
          workScopeCountry: mergedData.workScopeCountry || '',
          phone: mergedData.phone || mergedData.phoneNumber || '',
          linkedin: mergedData.linkedin || '',
          twitter: mergedData.twitter || '',
          expertise: mergedData.expertise || [],
          interests: mergedData.interests || [],
          learningGoals: mergedData.learningGoals || [],
          languages: mergedData.languages || [],
          selectedRegions: mergedData.selectedRegions || [],
          availability: mergedData.availability || {
            days: [],
            timeBlocks: []
          },
          goals: mergedData.goals || {},
          contentPrefs: mergedData.contentPrefs || {},
          eventPrefs: mergedData.eventPrefs || {},
          matchPrefs: mergedData.matchPrefs || {},
          socialSharingEnabled: mergedData.socialSharingEnabled || false,
          sharingFrequency: mergedData.sharingFrequency || 3,
          communityShareEnabled: mergedData.communityShareEnabled || false,
          communityShareGroup: mergedData.communityShareGroup || '',
          communityShareBlocked: mergedData.communityShareBlocked || [],
          showcase: mergedData.showcase || {
            profile: {},
            story: {},
            business: {},
            engagement: {},
            partnerships: {},
            community: {},
            order: ['profile','story','business','engagement','partnerships','community']
          },
          favoriteLocations: mergedData.favoriteLocations || userProfile.favoriteLocations || []
        });
        
        // Initialize card order
        const existingOrder = mergedData.showcase?.order || ['profile','story','business','engagement','partnerships','community'];
        const customCards = Object.keys(mergedData.showcase || {}).filter(key => 
          key.startsWith('custom_') && key !== 'order'
        );
        setCardOrder([...existingOrder, ...customCards]);
      } catch (error) {
        console.error('❌ Error loading profile data from profiles collection:', error);
        // Fallback to userProfile only if profiles collection fails
        const firstName = userProfile.firstName || userProfile.onboardingData?.firstName || '';
        const lastName = userProfile.lastName || userProfile.onboardingData?.lastName || '';
        const computedDisplayName = userProfile.displayName || userProfile.onboardingData?.displayName || `${firstName} ${lastName}`.trim();
        
        setFormData({
          firstName,
          lastName,
          displayName: computedDisplayName,
          bio: userProfile.bio || '',
          company: userProfile.company || '',
          companyLocation: userProfile.companyLocation || '',
          website: userProfile.website || '',
          location: userProfile.location || '',
          postcode: userProfile.postcode || '',
          city: userProfile.city || '',
          region: userProfile.region || '',
          country: userProfile.country || '',
          workScope: userProfile.workScope || 'lokaal',
          workScopeCountry: userProfile.workScopeCountry || '',
          phone: userProfile.phone || userProfile.phoneNumber || '',
          linkedin: userProfile.linkedin || '',
          twitter: userProfile.twitter || '',
          expertise: userProfile.expertise || [],
          interests: userProfile.interests || [],
          learningGoals: userProfile.learningGoals || [],
          languages: userProfile.languages || [],
          selectedRegions: userProfile.selectedRegions || [],
          availability: userProfile.availability || {
            days: [],
            timeBlocks: []
          },
          goals: userProfile.goals || {},
          contentPrefs: userProfile.contentPrefs || {},
          eventPrefs: userProfile.eventPrefs || {},
          matchPrefs: userProfile.matchPrefs || {},
          socialSharingEnabled: userProfile.socialSharingEnabled || false,
          sharingFrequency: userProfile.sharingFrequency || 3,
          communityShareEnabled: userProfile.communityShareEnabled || false,
          communityShareGroup: userProfile.communityShareGroup || '',
          communityShareBlocked: userProfile.communityShareBlocked || [],
          showcase: userProfile.showcase || {
            profile: {},
            story: {},
            business: {},
            engagement: {},
            partnerships: {},
            community: {},
            order: ['profile','story','business','engagement','partnerships','community']
          }
        });
      }
    };
    
    loadAllProfileData();
  }, [user, userProfile]);

  // Live completion score based on edited form values merged over profile
  const completion = useMemo(() => {
    const merged = { ...userProfile, ...formData };
    return computeProfileCompletion(merged);
  }, [userProfile, formData]);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      // ✅ CRITICAL: Auto-compute displayName when firstName or lastName changes
      const computedDisplayName = `${formData.firstName} ${formData.lastName}`.trim() || formData.displayName;
      
      // Auto-geocode companyLocation to save officeLocation with exact coordinates
      let officeLocationData: { name: string; address: string; coordinates?: { lat: number; lng: number } } | undefined;
      const locationToGeocode = formData.companyLocation?.trim() || formData.location?.trim() || [formData.city, formData.country].filter(Boolean).join(', ');
      
      if (locationToGeocode) {
        try {
          const geocoded = await geocodeAddress(locationToGeocode);
          if (geocoded) {
            officeLocationData = {
              name: formData.company || computedDisplayName || 'Kantoor',
              address: locationToGeocode,
              coordinates: { lat: geocoded.lat, lng: geocoded.lng }
            };
            console.log('✅ Office location geocoded:', officeLocationData);
          }
        } catch (err) {
          console.warn('Could not geocode office location:', err);
        }
      }

      // ✅ FIX: Save to userProfiles collection (basis profiel data)
      const userProfilesRef = doc(db, 'userProfiles', user.uid);
      await setDoc(userProfilesRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: computedDisplayName,
        bio: formData.bio,
        company: formData.company,
        companyLocation: formData.companyLocation,
        website: formData.website,
        location: formData.location,
        postcode: formData.postcode,
        city: formData.city,
        region: formData.region,
        country: formData.country,
        workScope: formData.workScope,
        workScopeCountry: formData.workScopeCountry,
        phone: formData.phone,
        linkedin: formData.linkedin,
        twitter: formData.twitter,
        expertise: formData.expertise,
        interests: formData.interests,
        learningGoals: formData.learningGoals,
        languages: formData.languages,
        selectedRegions: formData.selectedRegions,
        availability: formData.availability,
        socialSharingEnabled: formData.socialSharingEnabled,
        sharingFrequency: formData.sharingFrequency,
        communityShareEnabled: formData.communityShareEnabled,
        communityShareGroup: formData.communityShareGroup,
        communityShareBlocked: formData.communityShareBlocked,
        showcase: {
          ...formData.showcase,
          order: cardOrder
        },
        favoriteLocations: formData.favoriteLocations || [],
        ...(officeLocationData ? { officeLocation: officeLocationData } : {}),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // ✅ FIX: Also save goals, contentPrefs, eventPrefs, matchPrefs to profiles collection
      const profilesRef = doc(db, 'profiles', user.uid);
      await setDoc(profilesRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: computedDisplayName,
        goals: formData.goals,
        learningGoals: formData.learningGoals,
        contentPrefs: formData.contentPrefs,
        eventPrefs: formData.eventPrefs,
        matchPrefs: formData.matchPrefs,
        availability: formData.availability,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      console.log('✅ Profile data saved to both userProfiles and profiles collections');
      
      // Update the parent component with the new profile data
      if (onProfileUpdate) {
        const updatedProfile = { ...userProfile, ...formData, displayName: computedDisplayName };
        onProfileUpdate(updatedProfile);
        
        // Force reload profile data from Firebase after a short delay
        setTimeout(async () => {
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const profileRef = doc(db, 'userProfiles', user.uid);
            const profileSnap = await getDoc(profileRef);
            if (profileSnap.exists()) {
              const freshData = profileSnap.data() as UserProfile;
              onProfileUpdate(freshData);
            }
          } catch (error) {
            console.error('Error reloading profile:', error);
          }
        }, 500);
      }
      
      // Show success message
      alert('Profiel succesvol bijgewerkt!');
      // Don't close the modal - stay on the page so user can see the changes
      // onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Er is een fout opgetreden bij het opslaan van je profiel.');
    } finally {
      setSaving(false);
    }
  };

  const updateArrayField = (field: keyof EditableProfileData, index: number, value: string) => {
    if (Array.isArray(formData[field])) {
      const newArray = [...(formData[field] as string[])];
      newArray[index] = value;
      setFormData(prev => ({ ...prev, [field]: newArray }));
    }
  };

  const addArrayField = (field: keyof EditableProfileData, value: string) => {
    if (Array.isArray(formData[field])) {
      setFormData(prev => ({ 
        ...prev, 
        [field]: [...(prev[field] as string[]), value] 
      }));
    }
  };

  const removeArrayField = (field: keyof EditableProfileData, index: number) => {
    if (Array.isArray(formData[field])) {
      const newArray = (formData[field] as string[]).filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, [field]: newArray }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={`${embedded ? 'bg-white rounded-2xl border border-gray-200 p-6' : 'min-h-screen bg-gray-50 p-6'}`}>
      <div className={embedded ? '' : 'max-w-5xl mx-auto'}>
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar detached */}
          <aside className="col-span-12 md:col-span-4 lg:col-span-3 space-y-4">
            <div className="bg-white rounded-lg shadow-sm border p-4 min-h-[116px]">
              <div className="flex items-center space-x-3 mb-2">
                <img
                  src={userProfile?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || 'User')}&background=cccccc&color=fff&size=128`}
                  alt="Profile"
                  className="w-12 h-12 rounded-full object-cover"
                  style={{
                    objectPosition: userProfile?.photoPosition 
                      ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` 
                      : 'center'
                  }}
                />
                <div>
                  <h3 className="font-semibold">{userProfile?.displayName || 'Gebruiker'}</h3>
                  <p className="text-sm text-gray-500">Ondernemer</p>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {(userProfile as any)?.posts || 0} posts
                {canSeePlatformOnlineStats(user?.email || userProfile?.email)
                  ? ` • ${(userProfile as any)?.online?.length || 0} online`
                  : ''}
                {' '}• {(userProfile as any)?.connections || 0} connecties
              </div>
            </div>

            {settingsList.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSettingsKey(s.key)}
                className={`w-full text-left bg-white border rounded-lg p-4 hover:shadow transition ${activeSettingsKey === s.key ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className="flex items-center space-x-2">
                  {s.icon}
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">{s.title}</h3>
                    <p className="text-sm text-gray-500">{s.subtitle}</p>
                  </div>
                </div>
              </button>
            ))}
          </aside>

          {/* Main panel */}
          <main className="col-span-12 md:col-span-8 lg:col-span-9 space-y-6">
            <header className="mb-2">
              <h2 className="text-2xl font-semibold tracking-[-0.01em] text-gray-900">Instellingen</h2>
              <p className="text-sm text-gray-600">Beheer je account en voorkeuren</p>
            </header>

            <div className="space-y-6">
              {/* Profile Completion */}
              <SectionCard title="Profiel‑voltooiing" icon="✅">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Voltooid</span>
                    <span className="text-sm font-semibold text-gray-900">{completion.percentage}%</span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full border border-gray-200 overflow-hidden">
                    <div
                      className="h-3 bg-gradient-to-r from-[#7A9488] to-blue-500"
                      style={{ width: `${completion.percentage}%` }}
                    />
                  </div>
                  {completion.missing.length > 0 ? (
                    <div className="mt-2">
                      <div className="text-sm text-gray-700 mb-2">Ontbrekende gegevens:</div>
                      <ul className="list-disc list-inside text-sm text-gray-600">
                        {completion.missing.map((m) => (
                          <li key={m.key}>{m.label}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-sm text-green-700">Mooi! Je profiel is compleet.</div>
                  )}
                </div>
              </SectionCard>

              {/* Center content */}
              <section className="space-y-6">
                  {activeSettingsKey === 'profileInfo' && (
                    <SectionCard title="Basis Informatie" icon="👤">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* ✅ FIX: Naam velden */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Voornaam</label>
                              <input 
                                type="text" 
                                value={formData.firstName} 
                                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))} 
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                                placeholder="Je voornaam" 
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Achternaam</label>
                              <input 
                                type="text" 
                                value={formData.lastName} 
                                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))} 
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                                placeholder="Je achternaam" 
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                            <RichTextEditor
                              value={formData.bio}
                              onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                              placeholder="Vertel iets over jezelf..."
                              className="w-full"
                              style={{ minHeight: '100px' }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bedrijf</label>
                            <input type="text" value={formData.company} onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Je bedrijf of organisatie" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Vestigingslocatie</label>
                              <input type="text" value={formData.companyLocation} onChange={(e) => setFormData(prev => ({ ...prev, companyLocation: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Stad, Land" />
                              <p className="text-xs text-gray-500 mt-1">Plaats waar je bedrijf officieel gevestigd is (zoals op KvK/website).</p>
                          </div>
                          
                          {/* Favoriete Locaties */}
                          <div className="border-t pt-4 mt-4">
                            <FavoriteLocationsManager
                              favoriteLocations={formData.favoriteLocations || []}
                              onUpdate={(locations) => setFormData(prev => ({ ...prev, favoriteLocations: locations }))}
                              maxLocations={2}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                            <input type="url" value={formData.website} onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="https://jouwwebsite.nl" />
                          </div>
                        </div>
                        
                        {/* ✅ FIX: Locatie details sectie */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">📍 Locatie Details</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Postcode</label>
                              <input type="text" value={formData.postcode} onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="1234 AB" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Stad</label>
                              <input type="text" value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Amsterdam" />
                              <p className="text-xs text-gray-500 mt-1">Je huidige werk/woonstad voor matching (mag afwijken van vestigingslocatie).</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Provincie</label>
                              <input type="text" value={formData.region} onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Noord-Holland" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Land</label>
                              <input type="text" value={formData.country} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Nederland" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Locatie (algemeen)</label>
                              <input type="text" value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Stad, Land" />
                            </div>
                          </div>
                        </div>
                        
                        {/* ✅ FIX: Werkgebied sectie */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">🌍 Werkgebied</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Werkgebied</label>
                              <select value={formData.workScope} onChange={(e) => setFormData(prev => ({ ...prev, workScope: e.target.value as 'lokaal' | 'nationaal' | 'internationaal' }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                <option value="lokaal">Lokaal</option>
                                <option value="nationaal">Nationaal</option>
                                <option value="internationaal">Internationaal</option>
                              </select>
                            </div>
                            {formData.workScope === 'nationaal' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Land voor nationaal werkgebied</label>
                                <input type="text" value={formData.workScopeCountry} onChange={(e) => setFormData(prev => ({ ...prev, workScopeCountry: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Nederland" />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* ✅ FIX: Expertise & Interesses sectie */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">🎯 Expertise & Interesses</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">Expertise</label>
                                <button onClick={() => addArrayField('expertise', '')} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Toevoegen</button>
                              </div>
                              <div className="space-y-2">
                                {formData.expertise.length > 0 ? (
                                  formData.expertise.map((exp, index) => (
                                    <div key={index} className="flex gap-2">
                                      <input 
                                        type="text" 
                                        value={exp} 
                                        onChange={(e) => updateArrayField('expertise', index, e.target.value)} 
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" 
                                        placeholder="Bijv. Growth Marketing" 
                                      />
                                      <button onClick={() => removeArrayField('expertise', index)} className="px-3 py-2 text-red-600 hover:text-red-800 text-sm">✕</button>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 italic">Geen expertise toegevoegd</p>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">Interesses</label>
                                <button onClick={() => addArrayField('interests', '')} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Toevoegen</button>
                              </div>
                              <div className="space-y-2">
                                {formData.interests.length > 0 ? (
                                  formData.interests.map((interest, index) => (
                                    <div key={index} className="flex gap-2">
                                      <input 
                                        type="text" 
                                        value={interest} 
                                        onChange={(e) => updateArrayField('interests', index, e.target.value)} 
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" 
                                        placeholder="Bijv. AI, Podcasting" 
                                      />
                                      <button onClick={() => removeArrayField('interests', index)} className="px-3 py-2 text-red-600 hover:text-red-800 text-sm">✕</button>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 italic">Geen interesses toegevoegd</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* ✅ FIX: Leerdoelen sectie */}
                          <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">Leerdoelen</label>
                              <button onClick={() => addArrayField('learningGoals', '')} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Toevoegen</button>
                            </div>
                            <div className="space-y-2">
                              {formData.learningGoals.length > 0 ? (
                                formData.learningGoals.map((goal, index) => (
                                  <div key={index} className="flex gap-2">
                                    <input 
                                      type="text" 
                                      value={goal} 
                                      onChange={(e) => updateArrayField('learningGoals', index, e.target.value)} 
                                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" 
                                      placeholder="Bijv. Marketing automatisering" 
                                    />
                                    <button onClick={() => removeArrayField('learningGoals', index)} className="px-3 py-2 text-red-600 hover:text-red-800 text-sm">✕</button>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-gray-500 italic">Geen leerdoelen toegevoegd</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* ✅ Content Voorkeuren sectie */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">📚 Content Voorkeuren</h4>
                          <p className="text-sm text-gray-600 mb-4">
                            Geef aan welke formaten je het liefst consumeert. Dit helpt ons je relevantere content te tonen.
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">Voorkeur formaat (meerdere keuzes mogelijk)</label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {[
                                { id: 'video', label: 'Video', icon: '📹' },
                                { id: 'audio', label: 'Audio/Podcast', icon: '🎧' },
                                { id: 'article', label: 'Artikelen', icon: '📄' },
                                { id: 'live', label: 'Live sessies', icon: '🎥' }
                              ].map(format => {
                                const isSelected = formData.contentPrefs?.formatPrefs?.includes(format.id) || false;
                                return (
                                  <button
                                    key={format.id}
                                    type="button"
                                    onClick={() => {
                                      const currentPrefs = formData.contentPrefs?.formatPrefs || [];
                                      const newPrefs = isSelected
                                        ? currentPrefs.filter(f => f !== format.id)
                                        : [...currentPrefs, format.id];
                                      setFormData(prev => ({
                                        ...prev,
                                        contentPrefs: {
                                          ...prev.contentPrefs,
                                          formatPrefs: newPrefs
                                        }
                                      }));
                                    }}
                                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                                      isSelected
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    <div className="text-2xl mb-1">{format.icon}</div>
                                    <div className="text-sm font-medium">{format.label}</div>
                                  </button>
                                );
                              })}
                            </div>
                            {formData.contentPrefs?.formatPrefs && formData.contentPrefs.formatPrefs.length === 0 && (
                              <p className="text-xs text-[#7A9488] mt-2">💡 Tip: Selecteer minimaal één voorkeur voor betere content aanbevelingen</p>
                            )}
                          </div>
                        </div>
                        
                        {/* ✅ FIX: Doelstellingen & KPIs sectie */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">🎯 Doelstellingen & KPIs</h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Doelstellingen</label>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {[
                                  { key: 'clients', label: 'Klanten werven' },
                                  { key: 'partnerships', label: 'Partnerships' },
                                  { key: 'hiring', label: 'Werving' },
                                  { key: 'funding', label: 'Funding' },
                                  { key: 'speaking', label: 'Spreken' },
                                  { key: 'content', label: 'Content' },
                                  { key: 'personalBrand', label: 'Personal Brand' },
                                  { key: 'productLaunch', label: 'Product Launch' },
                                  { key: 'community', label: 'Community' }
                                ].map((goal) => {
                                  const goalKey = goal.key as keyof typeof formData.goals;
                                  const isChecked = typeof formData.goals?.[goalKey] === 'boolean' 
                                    ? (formData.goals[goalKey] as boolean) 
                                    : false;
                                  
                                  return (
                                    <label key={goal.key} className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => setFormData(prev => ({
                                          ...prev,
                                          goals: {
                                            ...prev.goals,
                                            [goal.key]: e.target.checked
                                          }
                                        }))}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      />
                                      <span className="text-sm text-gray-700">{goal.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Tijdsframe</label>
                                <select
                                  value={formData.goals?.timeframe || '0-3m'}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    goals: {
                                      ...prev.goals,
                                      timeframe: e.target.value
                                    }
                                  }))}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="0-3m">0-3 maanden</option>
                                  <option value="3-6m">3-6 maanden</option>
                                  <option value="6-12m">6-12 maanden</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notities</label>
                                <input
                                  type="text"
                                  value={formData.goals?.notes || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    goals: {
                                      ...prev.goals,
                                      notes: e.target.value
                                    }
                                  }))}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Extra notities over je doelen..."
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">KPIs (Key Performance Indicators)</label>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Leads</label>
                                  <input
                                    type="number"
                                    value={formData.goals?.kpis?.leads || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      goals: {
                                        ...prev.goals,
                                        kpis: {
                                          ...prev.goals?.kpis,
                                          leads: e.target.value ? parseInt(e.target.value) : undefined
                                        }
                                      }
                                    }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Revenue (€)</label>
                                  <input
                                    type="number"
                                    value={formData.goals?.kpis?.revenue || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      goals: {
                                        ...prev.goals,
                                        kpis: {
                                          ...prev.goals?.kpis,
                                          revenue: e.target.value ? parseInt(e.target.value) : undefined
                                        }
                                      }
                                    }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Followers</label>
                                  <input
                                    type="number"
                                    value={formData.goals?.kpis?.followers || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      goals: {
                                        ...prev.goals,
                                        kpis: {
                                          ...prev.goals?.kpis,
                                          followers: e.target.value ? parseInt(e.target.value) : undefined
                                        }
                                      }
                                    }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* ✅ FIX: Regio selectie "Jouw Werkgebied" (zoals in afbeelding) */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">📍 Jouw Werkgebied</h4>
                          <div className="space-y-4">
                            {/* Nederland Sectie */}
                            <div>
                              <button
                                onClick={() => setExpandedRegionSection(expandedRegionSection === 'nederland' ? null : 'nederland')}
                                className="w-full flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors mb-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span>🇳🇱</span>
                                  <span className="text-sm font-medium text-gray-700">Nederland</span>
                                  <span className="text-xs text-gray-500">
                                    ({formData.selectedRegions.filter(r => NEDERLAND_REGIONS.includes(r)).length}/{NEDERLAND_REGIONS.length})
                                  </span>
                                </div>
                                <span className="text-gray-400 text-sm">
                                  {expandedRegionSection === 'nederland' ? '▼' : '▶'}
                                </span>
                              </button>

                              {expandedRegionSection === 'nederland' && (
                                <div className="mt-2 grid grid-cols-2 gap-2 px-2">
                                  {NEDERLAND_REGIONS.map(region => (
                                    <label
                                      key={region}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                                        formData.selectedRegions.includes(region)
                                          ? 'bg-blue-100 border-2 border-blue-400'
                                          : 'bg-white border border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={formData.selectedRegions.includes(region)}
                                        onChange={(e) => {
                                          const newRegions = e.target.checked
                                            ? [...formData.selectedRegions, region]
                                            : formData.selectedRegions.filter(r => r !== region);
                                          setFormData(prev => ({ ...prev, selectedRegions: newRegions }));
                                        }}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                                      />
                                      <span className="text-sm text-gray-700">{region}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Internationaal Sectie */}
                            <div>
                              <button
                                onClick={() => setExpandedRegionSection(expandedRegionSection === 'internationaal' ? null : 'internationaal')}
                                className="w-full flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors mb-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span>🌍</span>
                                  <span className="text-sm font-medium text-gray-700">Internationaal</span>
                                  <span className="text-xs text-gray-500">
                                    ({formData.selectedRegions.filter(r => INTERNATIONAL_REGIONS.includes(r)).length}/{INTERNATIONAL_REGIONS.length})
                                  </span>
                                </div>
                                <span className="text-gray-400 text-sm">
                                  {expandedRegionSection === 'internationaal' ? '▼' : '▶'}
                                </span>
                              </button>

                              {expandedRegionSection === 'internationaal' && (
                                <div className="mt-2 grid grid-cols-2 gap-2 px-2">
                                  {INTERNATIONAL_REGIONS.map(region => (
                                    <label
                                      key={region}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                                        formData.selectedRegions.includes(region)
                                          ? 'bg-blue-100 border-2 border-blue-400'
                                          : 'bg-white border border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={formData.selectedRegions.includes(region)}
                                        onChange={(e) => {
                                          const newRegions = e.target.checked
                                            ? [...formData.selectedRegions, region]
                                            : formData.selectedRegions.filter(r => r !== region);
                                          setFormData(prev => ({ ...prev, selectedRegions: newRegions }));
                                        }}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                                      />
                                      <span className="text-sm text-gray-700">{region}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {formData.selectedRegions.length === 0 && (
                              <p className="text-sm text-gray-500 italic text-center py-2">Geen regio geselecteerd</p>
                            )}
                            {formData.selectedRegions.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs text-green-600 flex items-center gap-1 justify-center">
                                  <span>✓</span>
                                  <span>Wijzigingen worden automatisch opgeslagen</span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </SectionCard>
                  )}

                  {/* contact sectie verwijderd; velden zitten nu onder Headline editor */}

                  {activeSettingsKey === 'sharing' && (
                    <SectionCard title="Social Sharing" icon="📱">
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <input type="checkbox" id="socialSharingEnabled" checked={formData.socialSharingEnabled} onChange={(e) => setFormData(prev => ({ ...prev, socialSharingEnabled: e.target.checked }))} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                          <label htmlFor="socialSharingEnabled" className="ml-2 block text-sm text-gray-900">Social sharing inschakelen</label>
                        </div>
                        {formData.socialSharingEnabled && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sharing frequentie</label>
                            <select value={formData.sharingFrequency} onChange={(e) => setFormData(prev => ({ ...prev, sharingFrequency: parseInt(e.target.value) as 3 | 5 | 7 }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                              <option value={3}>3 keer per week</option>
                              <option value={5}>5 keer per week</option>
                              <option value={7}>7 keer per week</option>
                            </select>
                          </div>
                        )}

                        {/* Together Strong - Community share */}
                        <div className="border-t pt-4 space-y-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="communityShareEnabled"
                              checked={!!formData.communityShareEnabled}
                              onChange={(e) => setFormData(prev => ({ ...prev, communityShareEnabled: e.target.checked }))}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="communityShareEnabled" className="ml-2 block text-sm text-gray-900">
                              Together Strong: community share inschakelen
                            </label>
                          </div>
                          {formData.communityShareEnabled && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Groepsnaam</label>
                                <input type="text" value={formData.communityShareGroup || ''} onChange={(e) => setFormData(prev => ({ ...prev, communityShareGroup: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. SuperKonnected NL" />
                              </div>

                              {/* Blocklist manager */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Geblokkeerde leden (UID of email)</label>
                                <div className="space-y-2">
                                  {(formData.communityShareBlocked || []).map((uid, index) => (
                                    <div key={index} className="flex gap-2">
                                      <input type="text" value={uid} onChange={(e) => {
                                        const next = [...(formData.communityShareBlocked || [])];
                                        next[index] = e.target.value;
                                        setFormData(prev => ({ ...prev, communityShareBlocked: next }));
                                      }} className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="uid-of-email" />
                                      <button onClick={() => setFormData(prev => ({ ...prev, communityShareBlocked: (prev.communityShareBlocked || []).filter((_, i) => i !== index) }))} className="px-3 py-2 text-red-600 hover:text-red-800">✕</button>
                                    </div>
                                  ))}
                                  <button onClick={() => setFormData(prev => ({ ...prev, communityShareBlocked: [ ...(prev.communityShareBlocked || []), '' ] }))} className="text-blue-600 hover:text-blue-800 text-sm">+ Blokkade toevoegen</button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </SectionCard>
                  )}

                  {/* skills sectie verwijderd; velden zitten nu onder Headline editor */}

                  {/* Kaarten Beheer - Uitgeschakeld voor nu */}
                  {/* {activeSettingsKey === 'cards' && (
                    <SectionCard title="Kaarten Beheer" icon="🎴">
                      <div className="p-6 bg-pink-50 border border-pink-100 rounded-xl text-center space-y-4">
                        <p className="text-base text-pink-900">
                          Personaliseer je verhaal en content kaarten in de dedicated kaarten editor.
                        </p>
                        <p className="text-sm text-pink-700">
                          Alle kaartbewerking (volgorde, media, CTA's) gebeurt daar, zodat deze instellingen schoon blijven.
                        </p>
                        <button
                          onClick={() => {
                            try {
                              window.location.hash = '#cards-editor';
                            } catch (error) {
                              console.warn('Could not navigate to cards editor:', error);
                            }
                          }}
                          className="w-full md:w-auto px-6 py-3 bg-pink-600 hover:bg-pink-700 text-white rounded-lg font-semibold transition-colors shadow-md"
                        >
                          Open Kaarten Editor
                        </button>
                      </div>
                    </SectionCard>
                  )} */}
              </section>
            </div>

            {/* Save Button */}
            <div className="mt-8 flex justify-end space-x-3">
              {onClose && (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;





